import * as XLSX from "xlsx";
import { MIN_KEYWORD_LENGTH } from "./keywordValidation";

/** Splits pasted/typed text into unique, normalized keywords (comma or newline separated). */
export function parseKeywordsFromText(value: string): string[] {
  const keywords = value
    .split(/[\n,]+/)
    .map((kw) => kw.trim().toLowerCase())
    .filter((kw) => kw.trim().length >= MIN_KEYWORD_LENGTH);

  return [...new Set(keywords)];
}

const isHeaderCell = (value: string) => value === "keyword" || value === "keywords";

export interface ParseKeywordsFromFileResult {
  keywords: string[];
  skippedCount: number;
}

/** Parses column A of a CSV or XLSX/XLS file into unique, normalized keywords, skipping a header row if present. */
export async function parseKeywordsFromFile(file: File): Promise<ParseKeywordsFromFileResult> {
  const fileExtension = file.name.split(".").pop()?.toLowerCase();

  if (fileExtension === "csv" || fileExtension === "xlsx" || fileExtension === "xls") {
    const workbook = fileExtension === "csv"
      ? XLSX.read(await file.text(), { type: "string" })
      : XLSX.read(await file.arrayBuffer(), { type: "array" });

    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");
    const keywords: string[] = [];
    let skippedCount = 0;

    for (let row = range.s.r; row <= range.e.r; row++) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: 0 });
      const cell = worksheet[cellAddress];
      if (cell && cell.v) {
        const keyword = String(cell.v).trim().toLowerCase();
        if (!keyword || isHeaderCell(keyword)) continue;
        if (keyword.trim().length >= MIN_KEYWORD_LENGTH) {
          keywords.push(keyword);
        } else {
          skippedCount++;
        }
      }
    }

    return { keywords: [...new Set(keywords)], skippedCount };
  }

  return { keywords: [], skippedCount: 0 };
}

export function downloadKeywordTemplateCsv() {
  const csvContent = "keyword\nexample keyword 1\nexample keyword 2\nexample keyword 3";
  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "keywords_template.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
